import React from 'react';
import { legal } from '~/marketing/legalData';
import { Seo } from './Seo';

/* Shared chrome and typography for the four legal pages.

   It exists so the four agree on what a legal page looks like, and so the
   entity block at the bottom is written once. It is app-authored and composes
   nothing from `components/` beyond what the marketing layout already supplies,
   because a legal page is prose in a column — the design system has no
   primitive for that and inventing one here would be editing a file this
   project does not own.

   Prose is capped at 78ch rather than the 70ch the marketing pages use. Legal
   text is read in long runs by someone looking for one clause, and a narrower
   column makes a fourteen-section document taller without making it clearer. */

const sec = { display: 'grid', gap: 'var(--space-4)' };

export const P = ({ children }) => (
  <p style={{ color: 'var(--text-body)', maxWidth: '78ch', margin: 0 }}>{children}</p>
);

export const H2 = ({ children }) => (
  <div style={{ borderBottom: '1px solid var(--border-subtle)', paddingBottom: 'var(--space-3)' }}>
    <h2 style={{ fontSize: 'var(--fs-h3)', margin: 0 }}>{children}</h2>
  </div>
);

export const H3 = ({ children }) => (
  <h3 style={{ fontSize: 'var(--fs-body-lg)', margin: 0 }}>{children}</h3>
);

export const UL = ({ children }) => (
  <ul style={{ color: 'var(--text-body)', maxWidth: '78ch', margin: 0, paddingLeft: '1.25em', display: 'grid', gap: 'var(--space-2)' }}>
    {children}
  </ul>
);

export const Section = ({ title, children }) => (
  <section style={sec}>
    <H2>{title}</H2>
    {children}
  </section>
);

/* The one thing every legal page carries and none of them should word
   differently: who you are contracting with, and where they are. */
export const EntityBlock = () => (
  <div style={{ display: 'grid', gap: 'var(--space-2)', borderTop: '1px solid var(--border-subtle)', paddingTop: 'var(--space-5)' }}>
    <p className="sp-caption" style={{ margin: 0 }}>{legal.business}</p>
    <p className="sp-caption" style={{ margin: 0 }}>{legal.entityAddress}</p>
    <p className="sp-caption" style={{ margin: 0 }}>
      <a href={`mailto:${legal.supportEmail}`}>{legal.supportEmail}</a>
    </p>
  </div>
);

// `updated` is for a page that has changed since the others; without it the
// page carries the date all four were published on. **`null` leaves the date
// off**, for a page whose content is not a policy: on the contact page a date
// only suggested it changes often, the founder's call 2026-10-23. A default
// parameter fires for `undefined` alone, so `null` reaches the check below.
export function LegalPage({ title, lede, description, path, updated = legal.effectiveDate, children }) {
  return (
    <>
      <Seo title={title} description={description} path={path} />
      <main
        className="sp-page"
        style={{ paddingBlock: 'clamp(40px,5vw,80px)', display: 'grid', gap: 'var(--space-12)' }}
      >
        <header style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: '78ch' }}>
          <span className="sp-label">Legal</span>
          {/* 36px, 28px on a phone, above the 22px section headings. It asked
              for `--fs-display-3` until 2026-10-23, a token the design system
              never defined, so every legal title rendered at body size, 15px,
              under headings larger than it. `css_variables_test` holds it now. */}
          <h1 style={{ fontSize: 'var(--fs-h1)', lineHeight: 1.1, margin: 0 }}>{title}</h1>
          {updated && <p className="sp-caption" style={{ margin: 0 }}>Last updated {updated}</p>}
          {lede && <P>{lede}</P>}
        </header>
        {children}
        <EntityBlock />
      </main>
    </>
  );
}
